import clsx from "clsx";
import { useMemo, useState } from "react";

import Stepper from "components/Stepper";
import { INVITED_CARRIER_DESCRIPTION_LIST } from "constants/haulpay-invited-carrier";
import useDeviceType from "hooks/useDeviceType";
import OnboardingLayout from "layouts/Onboarding";
import { IFactoringCompany } from "models/HaulPay";
import InvitedCarrierBankInfo from "./BankInfo";
import SearchFactoringCompany from "./BankInfo/SearchFactoringCompany";
import InvitedCarrierInfo from "./CarrierInfo";
import InvitedCarrierComplete from "./Complete";
import OnboardingIntro from "./Intro";
import styles from "./style.module.scss";

interface ISectionProps {
  step: number;
  setStepCallback: (step: number) => void;
  setIsSearchFactoringCompany: React.Dispatch<React.SetStateAction<boolean>>;
  selectedFactoringCompany?: IFactoringCompany;
}

function SelectedSection({
  step,
  setStepCallback,
  setIsSearchFactoringCompany,
  selectedFactoringCompany
}: Readonly<ISectionProps>) {
  switch (step) {
    case 0:
      return <OnboardingIntro handleNext={(nextStep: number) => setStepCallback(nextStep)} />;

    case 1:
      return <InvitedCarrierInfo handleNext={() => setStepCallback(step + 1)} />;

    case 2:
      return (
        <InvitedCarrierBankInfo
          setIsSearchFactoringCompany={setIsSearchFactoringCompany}
          handleNext={() => setStepCallback(step + 1)}
          selectedFactoringCompany={selectedFactoringCompany}
        />
      );

    case 3:
      return <InvitedCarrierComplete />;

    default:
      return null;
  }
}

export default function InvitedCarrierOnboarding() {
  const [step, setStep] = useState(0);
  const { isDesktop } = useDeviceType();
  const [isSearchFactoringCompany, setIsSearchFactoringCompany] = useState(false);
  const [selectedFactoringCompany, setSelectedFactoringCompany] = useState<IFactoringCompany>();

  const { title, description } = useMemo(() => INVITED_CARRIER_DESCRIPTION_LIST[step], [step]);

  const handleCompanySelect = (company: IFactoringCompany) => {
    setSelectedFactoringCompany(company);
    setIsSearchFactoringCompany(false);
  };

  return !isDesktop && isSearchFactoringCompany ? (
    <SearchFactoringCompany
      company={selectedFactoringCompany}
      onCompanySelected={handleCompanySelect}
    />
  ) : (
    <OnboardingLayout className={styles.invitedCarrierContainer}>
      {step ? (
        <>
          <Stepper
            data={Array.from({ length: 3 }).map((_, i) => ({ id: i }))}
            currentStep={step - 1}
            setStep={setStep}
            className="z-20 mt-8"
          />

          <div className={clsx(styles.descriptionWrapper, step === 2 && "text-center gap-4")}>
            <h3 className="text-xl font-medium">{title}</h3>
            <span>{description}</span>
          </div>
        </>
      ) : null}

      <SelectedSection
        step={step}
        setStepCallback={setStep}
        setIsSearchFactoringCompany={setIsSearchFactoringCompany}
        selectedFactoringCompany={selectedFactoringCompany}
      />
    </OnboardingLayout>
  );
}
